import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
  inject,
  input,
  output,
} from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormBase } from 'src/app/_models/form-base';
import { ModalService } from 'src/app/_services/modal.service';
import { ButtonComponent } from '../../buttons/button/button.component';
import { CheckboxGroupComponent } from '../../selection-controls/checkbox-group/checkbox-group.component';
import { ChipRadioButtonComponent } from '../../selection-controls/chip-radio-button/chip-radio-button.component';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatIconButtonComponent } from '../../buttons/mat-icon-button/mat-icon-button.component';

@Component({
  selector: 'app-filter-modal',
  templateUrl: './filter-modal.component.html',
  styleUrl: './filter-modal.component.scss',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    ChipRadioButtonComponent,
    CheckboxGroupComponent,
    ButtonComponent,
    MatSlideToggle,
    MatIconButtonComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterModalComponent implements OnInit, OnDestroy {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly modalService = inject(ModalService);
  private readonly element: HTMLElement;

  /** Checkbox base field (optional) */
  statusField = input<FormBase<string> | undefined>(undefined);

  /** Select base field (optional) */
  modeField = input<FormBase<string> | undefined>(undefined);

  /** Checkbox base field (optional) */
  modulesField = input<FormBase<string> | undefined>(undefined);

  /** Checkbox base field (optional) */
  offlineField = input<FormBase<boolean> | undefined>(undefined);

  /** Checkbox base field (optional) */
  deletedField = input<FormBase<boolean> | undefined>(undefined);

  /** Parent form (required)*/
  parentForm = input<FormGroup>();

  /** Modal id (required) */
  id = input<string>();

  /** Title (optional) */
  title = input<FormBase<string> | undefined>(undefined);

  /** Primary button text (required)*/
  primaryButtonText = input<string>();

  /** Primary button id (required) */
  primaryButtonId = input<string>();

  /** Secondary button text (required) */
  secondaryButtonText = input<string>();

  /** Secondary button id (required) */
  secondaryButtonId = input<string>();

  /** Is the modal processing (optional, default: false) */
  isProcessing = input<boolean>(false);

  /** Hide deleted field (optional, default: false) */
  hideDeletedField = input<boolean>(false);

  /** Checkbox style (optional, default: 'default') */
  checkboxStyle = input<'default' | 'tabs' | 'tabs-small' | 'list'>('default');

  /** Whether user can close modal (optional, default: true) */
  allowClose = input<boolean>(true);

  /** Output event for clicked button on modal */
  modalButtonClicked = output<string>();

  /** Output event for clicked button on modal */
  moduleCheckboxClicked = output<Event>();

  constructor() {
    this.element = this.elementRef.nativeElement;
  }

  ngOnInit(): void {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const modal = this;

    // Close modal on background click - only if not processing a rejection
    if (this.allowClose()) {
      this.element.addEventListener('click', (e: Event) => {
        const target = e.target as HTMLElement;
        if (target.className === 'modal--container' && !this.isProcessing()) {
          modal.close();
        }
      });
    }
    // add self (this modal instance) to the modal service so it's accessible from controllers
    this.modalService.add(this);
    document.body.appendChild(this.element);
  }

  updateValue(event: Event): void {
    this.moduleCheckboxClicked.emit(event);
  }

  // open modal
  open(): void {
    this.element.style.display = 'block';
  }

  // close modal
  close(): void {
    // Reset valdiation text value and close modal
    this.element.style.display = 'none';
  }

  buttonEventHandler(valueEmitted: string): void {
    this.modalButtonClicked.emit(valueEmitted);
  }

  // Remove self from modal service when component is destroyed
  ngOnDestroy(): void {
    this.modalService.remove(this.id());
    this.element.remove();
  }
}
